import React, { useEffect, useState } from "react";
import { Button, Form, message } from "antd";
import { useDispatch } from "react-redux";
import CommonCard from "../../../global/card";
import CommonTabs from "../../../global/tabs";
import { useGenericQuery } from "../../../../queries/use-generic-query";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import LocalStorageService from "../../../../utils/localStorageServices";
import { showUserNotification } from "../../../../utils/functions";
import SOURCES from "../../../../utils/constants";
import EnhancedSettingsBasics from "./enhanced-settings-basics";
import EnhancedSettingsBusinessHours from "./enhanced-settings-business-hours";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import Spinner from "../../../global/spinner";
import { cn } from "../../../../utils/cn";

const localStorageServices = LocalStorageService.getService();

const EnhancedSettings = () => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState("1");
    const [settingMaxUploadLimit, setSettingMaxUploadLimit] = useState("");
    const [settingAutomaticallyAcceptLead, setSettingAutomaticallyAcceptLead] = useState("y");
    const [days, setDays] = useState([]);

    const dispatch = useDispatch();
    const currentUser = localStorageServices.getCurrentUser();
    const timeZone = currentUser?.user?.timezone?.name;
    const paymentplan = currentUser?.user?.paymentPlanId?.planCode;
    const defaultStartTime = "9:00";
    const defaultEndTime = "18:00";

    const resetDays = () => {
        setDays([
            { name: "Monday", day: "Mon", startTime: defaultStartTime, endTime: defaultEndTime },
            { name: "Tuesday", day: "Tue", startTime: defaultStartTime, endTime: defaultEndTime },
            { name: "Wednesday", day: "Wed", startTime: defaultStartTime, endTime: defaultEndTime },
            { name: "Thursday", day: "Thu", startTime: defaultStartTime, endTime: defaultEndTime },
            { name: "Friday", day: "Fri", startTime: defaultStartTime, endTime: defaultEndTime },
            { name: "Saturday", day: "Sat", startTime: defaultStartTime, endTime: defaultEndTime },
            { name: "Sunday", day: "Sun", startTime: defaultStartTime, endTime: defaultEndTime },
        ]);
    };

    const settingOptions = {
        settingKeys: {
            MAX_LEADS_UPLOAD_VIA_CSV: "MAX_LEADS_UPLOAD_VIA_CSV",
            ACCEPT_LEADS_IMPORTED_VIA_LEAD_FORM: "ACCEPT_LEADS_IMPORTED_VIA_LEAD_FORM",
            BUSSINESS_HOURS: "BUSSINESS_HOURS",
        },
        settingAutomaticallyAcceptLead: [
            { id: "y", val: "Yes" },
            { id: "n", val: "No" },
        ],
    };

    // Fetch settings
    const { data: settingsData, isLoading: isFetching } = useGenericQuery({
        endpoint: "user/settings",
        queryKey: ["user", "settings"],
        onSuccess: (data) => {
            if (data && Array.isArray(data)) {
                data.forEach((setting) => {
                    const { settingKey, settingValue } = setting;
                    switch (settingKey) {
                        case settingOptions.settingKeys.MAX_LEADS_UPLOAD_VIA_CSV:
                            setSettingMaxUploadLimit(settingValue);
                            form.setFieldsValue({ maxLeadCsvUploadLimit: settingValue });
                            break;
                        case settingOptions.settingKeys.ACCEPT_LEADS_IMPORTED_VIA_LEAD_FORM:
                            setSettingAutomaticallyAcceptLead(settingValue);
                            form.setFieldsValue({ acceptLeadViaLeadForm: settingValue });
                            break;
                        case settingOptions.settingKeys.BUSSINESS_HOURS:
                            setDays(settingValue);
                            break;
                        default:
                            break;
                    }
                });
            }
        }
    });

    // Save mutation
    const saveSettingsMutation = useGenericMutation({
        endpoint: "user/settings",
        onSuccess: () => {
            message.success("Settings saved successfully.");
        }
    });

    useEffect(() => {
        resetDays();
    }, []);

    const parseTimeString = (timeString) => {
        if (timeString) {
            const [hours, minutes] = timeString.split(":");
            return hours && minutes ? new Date(0, 0, 0, hours, minutes, 0, 0) : null;
        }
        return null;
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Validate business hours
            const invalidDays = days.filter(({ startTime, endTime }) => {
                const [start, end] = [parseTimeString(startTime), parseTimeString(endTime)];
                return !(start && end && start < end && (end - start) / (60 * 60 * 1000) <= 10);
            });

            if (invalidDays.length > 0) {
                const filteredInvalidDays = invalidDays.map((d) => d.day);
                dispatch(
                    showUserNotification(
                        `Business hours must not be greater than 10 hours on ${filteredInvalidDays.join(", ")}`,
                        "danger"
                    )
                );
                return;
            }

            const postData = {
                settings: [
                    {
                        settingKey: settingOptions.settingKeys.ACCEPT_LEADS_IMPORTED_VIA_LEAD_FORM,
                        settingValue: values.acceptLeadViaLeadForm,
                    },
                    {
                        settingKey: settingOptions.settingKeys.BUSSINESS_HOURS,
                        settingValue: days,
                    },
                ],
            };

            await saveSettingsMutation.mutateAsync(postData);
        } catch (error) {
            console.error("Validation failed:", error);
        }
    };

    const handleTimeChange = (idx, field, value) => {
        const updateTime = [...days];
        updateTime[idx][field] = value;
        setDays(updateTime);
    };

    const tabItems = [
        {
            key: "1",
            label: "Basics",
            children: (
                <EnhancedSettingsBasics
                    form={form}
                    settingMaxUploadLimit={settingMaxUploadLimit}
                    settingAutomaticallyAcceptLead={settingAutomaticallyAcceptLead}
                    setSettingAutomaticallyAcceptLead={setSettingAutomaticallyAcceptLead}
                    settingOptions={settingOptions}
                />
            )
        },
        [SOURCES.PAYMENT_PLAN_CODE.P_5.toString(), SOURCES.PAYMENT_PLAN_CODE.P_4.toString()].includes(paymentplan?.toString()) && {
            key: "2",
            label: "Business Hours",
            children: (
                <EnhancedSettingsBusinessHours
                    timeZone={timeZone}
                    days={days}
                    handleTimeChange={handleTimeChange}
                />
            )
        }
    ].filter(Boolean);

    return (
        <CommonCard
            isLoading={{
                show: isFetching, loaderCmp: <div className="flex flex-col items-center justify-center p-20 !min-h-[600px]">
                    <div className="relative size-12 mb-4">
                        <div className="absolute inset-0 rounded-full border-4 border-primary/10" />
                        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin" />
                    </div>
                    <p className="animate-pulse">Loading settings...</p>
                </div>
            }}
            className={cn("!p-0 overflow-hidden", isFetching && "min-h-[600px]")}
        >
            <CommonTabs
                items={tabItems}
                activeKey={activeTab}
                onChange={setActiveTab}
            />

            <div className="mt-8 flex justify-end border-t border-gray-100 pt-6">
                <Button
                    size="large"
                    onClick={handleSave}
                    className="h-11 px-8 rounded-lg font-semibold shadow-md !border-none hover:!border-none !bg-primary
                     hover:bg-primary/90 transition-all duration-200 !text-white"
                >
                    {saveSettingsMutation.isPending && <Spinner className={"!border-white"} />}
                    Save Settings
                </Button>
            </div>
        </CommonCard>
    );
};

export default EnhancedSettings;